import React, { useEffect, useContext, useState, useRef } from 'react'
import { Link as RouterLink, useHistory } from 'react-router-dom';

import Moment from 'moment'

import LanguageContext from "../../../contexts/LanguageContext"

import { Box, Hidden, Button, Drawer, Dialog, CircularProgress } from '@mui/material'

import checkGreenRoundIcon from "../../../images/checkGreenRoundIcon.svg"
import closeRedRoundIcon from "../../../images/closeRedRoundIcon.svg"
import sablierIcon from "../../../images/sablierIcon.svg"
import simpleArrowIcon from "../../../images/simpleArrowIcon.svg"
import closeIcon from '../../../images/closeIcon.svg';

import NewCertifChart from "../NewCertifChart"

Moment.locale('fr')

/* C3 (retours Hicham 03/09) — la legende sous le radar de certification.
 *
 * Elle nommait « Annee 1 / Annee 2 / Annee 3 » les trois styles de trait du
 * plan. Deux defauts : elle laissait croire a un seuil par annee alors qu'il
 * n'y en a qu'un, et le trait plein de « Annee 3 » etait indiscernable de celui
 * du seuil, ajoute le meme jour. Le radar n'oppose plus que deux choses : ce
 * que l'apprenant a acquis, et la barre a franchir.
 *
 * Elle vit ici, et la fiche laterale de Certification.js l'importe : c'est la
 * meme figure aux deux endroits, elle ne doit pas pouvoir diverger (motif B4 du
 * journal des erreurs — une regle corrigee sur un ecran et pas sur l'autre).
 *
 * Chaque entree se deduit des series REELLEMENT dessinees, jamais d'une
 * condition parallele qui pourrait diverger (motif M19 : un trait sans legende,
 * ou une legende sans trait). Un apprenant qui n'a encore rien passe n'a aucune
 * serie : la legende ne s'affiche pas du tout. Un groupe sans seuil configure
 * n'affiche pas l'entree du seuil (reponse Q2 — pas de valeur par defaut sur
 * une donnee qui appartient au client).
 */
export const LegendeRadar = ({ donnees }) => {
  const { actLanguage, languageValues } = useContext(LanguageContext)

  const series = donnees?.datasets || []
  const avecNiveau = series.some((d) => d?.estSeuil !== true)
  const avecSeuil = series.some((d) => d?.estSeuil === true)
  if (!avecNiveau && !avecSeuil) { return null }

  /* Le niveau acquis est dessine en trois couleurs, une par famille de
     competences — les memes que les pastilles autour du radar. Le temoin de
     legende les reprend toutes les trois plutot que d'en inventer une
     quatrieme. */
  const degradeFamilles = "linear-gradient(90deg,"
    + " rgba(255,81,6,1) 0%, rgba(255,81,6,1) 33%,"
    + " rgba(79,57,255,1) 33%, rgba(79,57,255,1) 66%,"
    + " rgba(243,144,255,1) 66%, rgba(243,144,255,1) 100%)"

  return (
    <Box sx={{width:"100%", justifyContent:"center", gap:["20px", "40px"], color:"text.black", marginTop:'-10px', marginBottom:"20px", fontSize:["11px", "13px", "13px", "13px"], display:"flex"}}>
      {avecNiveau &&
        <Box sx={{display:"flex", gap:"5px", alignItems:"center"}}>
          <Box sx={{width:['30px',' 40px'], height:"2px", flexShrink:0, background: degradeFamilles}} />
          <Box>{languageValues?.adminDashboard?.dashCertif?.niveauAcquisLegende?.[actLanguage]}</Box>
        </Box>
      }
      {avecSeuil &&
        <Box sx={{display:"flex", gap:"5px", alignItems:"center"}}>
          <Box sx={{width:['30px',' 40px'], borderTop:"2px solid rgba(10,10,11,0.5)"}} />
          <Box>{languageValues?.adminDashboard?.dashCertif?.seuilLegende?.[actLanguage]}</Box>
        </Box>
      }
    </Box>
  )
}

const DialogContent = ({ selectedID, onClose, dataLoaded, getDatasForChart, aUneCertification }) => {
  const { actLanguage, languageValues } = useContext(LanguageContext)

  /* Une seule lecture des donnees du radar : le graphe et sa legende doivent
     decrire exactement la meme figure. */
  const donneesRadar = selectedID !== null && selectedID !== undefined ? getDatasForChart(selectedID) : null

  return (
    <Box sx={{
      padding:["30px 25px", "30px 30px", "40px", "40px",]
    }}>
      <Hidden smDown>
        <Box sx={{display:"flex", justifyContent:"flex-end"}}>
          <img
            src={closeIcon}
            style={{width:"20px", height:"20px", cursor:"pointer"}}
            onClick={onClose}
          />
        </Box>
      </Hidden>
      <Hidden smUp>
        <Box sx={{marginBottom:"40px", display:"flex", flexDirection:"column", gap:"10px"}}>
          <Box sx={{color:"text.black", fontSize:"13px"}}>Certification</Box>
          <Box sx={{display:"flex", gap:"5px", alignItems:"center"}}>
            <Box
              sx={{
                width:"35px",
                height:"35px",
                minWidth:"35px",
                cursor:"pointer",
                display:"flex",
                alignItems:"center",
                justifyContent:"center",
              }}
              onClick={onClose}
            >
              <Box sx={{
                width:"19px",
              }}>
                <img src={simpleArrowIcon} style={{width:"100%", transform:"rotate(180deg)"}}/>
              </Box>
            </Box>
            <Box sx={{marginLeft:"10px", fontSize:"25px", color:"text.black", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", textTransform:"uppercase"}}>
              {selectedID?.userName}
            </Box>
          </Box>
        </Box>
      </Hidden>
      {selectedID !== null &&
        <Box sx={{display:"flex", flexDirection:"column", alignItems:"center", gap:"40px"}}>
          <Hidden smDown>
            <Box sx={{width:"100%", textTransform:"capitalize", color:"text.black", fontSize:["32px","32px","36px","36px"], textAlign:"center"}}>
              {selectedID.userName}
            </Box>
          </Hidden>
          <Box sx={{display:"flex", width:"100%", flexWrap:["wrap", "nowrap"], gap:"30px 0px"}}>
            {/* C1 : meme regle que le volet lateral et que le Suivi. */}
            {aUneCertification &&
              <Box sx={{width:['50%', '100%'], overflow:"hidden", textOverflow:"ellipsis", display:"flex", alignItems:["start", "center"], flexDirection:"column", gap:"10px"}}>
                {/* Meme correction que dans la fiche laterale : « Nom de diplome »
                    devient « Diplome », et cette case-ci — le diplome DU GROUPE —
                    prend le nom qu'elle porte deja dans le tableau et dans
                    l'export, « Certification ». Sans quoi le modal affichait deux
                    fois « Diplome », cote a cote, pour deux valeurs differentes. */}
                <Box sx={{fontSize:"15px", color:"text.subTitle", textAlign:"center"}}>
                  {languageValues?.adminDashboard?.monitoringTable?.certification[actLanguage]}
                </Box>
                <Box sx={{fontSize:"17px", color:"text.black", textAlign:"center"}}>
                  {selectedID?.diplome ? selectedID.diplome : "-"}
                </Box>
              </Box>
            }
            {/* F5 : le nom du diplome de l'apprenant, absent du modal mobile */}
            <Box sx={{width:['50%', '100%'], overflow:"hidden", textOverflow:"ellipsis", display:"flex", alignItems:["start", "center"], flexDirection:"column", gap:"10px"}}>
              <Box sx={{fontSize:"15px", color:"text.subTitle", textAlign:"center"}}>
                {languageValues?.adminDashboard?.monitoringTable?.sector[actLanguage]}
              </Box>
              <Box title={selectedID?.nomDiplome || ""} sx={{
                maxWidth:"100%",
                fontSize: String(selectedID?.nomDiplome || "").length > 18 ? "15px" : "17px",
                color:"text.black", textAlign:"center",
                whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis",
              }}>
                {selectedID?.nomDiplome ? selectedID.nomDiplome : "-"}
              </Box>
            </Box>
            <Box sx={{width:['50%', '100%'], overflow:"hidden", textOverflow:"ellipsis", display:"flex", alignItems:["start", "center"], flexDirection:"column", gap:"10px"}}>
              <Box sx={{maxWidth:"100%", fontSize:"15px", color:"text.subTitle", textAlign:"center", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis"}}>
                {/* `anneeDiplome` porte l'annee d'ETUDE (Certification.js:436),
                    pas une annee de certification : meme libelle que la fiche
                    laterale et que la colonne du tableau. */}
                {languageValues?.adminDashboard?.monitoringTable?.studyYear[actLanguage]}
              </Box>
              <Box style={{fontSize:"17px", color:"text.black", textAlign:"center"}}>
                {selectedID?.anneeDiplome ? selectedID.anneeDiplome : "-"}
              </Box>
            </Box>
            <Box sx={{width:['50%', '100%'], overflow:"hidden", textOverflow:"ellipsis", display:"flex", alignItems:["start", "center"], flexDirection:"column", gap:"10px"}}>
              <Box sx={{fontSize:"15px", color:"text.subTitle", textAlign:"center"}}>
                {languageValues?.adminDashboard?.monitoringTable?.progress[actLanguage]}
              </Box>
              <Box sx={{color:"text.black", fontSize:"17px", textAlign:"center"}}>
                {selectedID?.status === "ended" &&
                  <Box sx={{display:"flex", alignItems:"center", gap:"6px", height:"100%",}}>
                    <img src={checkGreenRoundIcon} style={{width:"20px"}}/>
                    <Box sx={{fontSize:"17px"}}>
                      <Hidden only={["sm"]}>
                        <font>
                          {languageValues?.adminDashboard?.monitoringTable?.statusEnded[actLanguage]}
                        </font>
                      </Hidden>
                    </Box>
                  </Box>
                }
                {selectedID?.status === "inProgress" &&
                  <Box sx={{display:"flex", alignItems:"center", gap:"6px", height:"100%"}}>
                    <Box sx={{position:"relative", height:"20px"}}>
                      <CircularProgress variant="determinate" value={100} thickness={5.5} size="20px" sx={{color: '#E7E7E7'}} />
                      <CircularProgress
                        variant="determinate"
                        value={selectedID.statusProgress}
                        thickness={6}
                        size="20px"
                        sx={{
                          color: "black",
                          position: 'absolute',
                          left: 0,
                        }}
                      />
                    </Box>
                    <Box sx={{fontSize:"17px"}}>
                      <Hidden mdDown>
                        {languageValues?.adminDashboard?.monitoringTable?.statusProgress[actLanguage]}
                        {selectedID.statusProgress}%
                      </Hidden>
                      <Hidden mdUp>
                        {selectedID.statusProgress}%
                      </Hidden>
                    </Box>
                  </Box>
                }
                {selectedID?.status === "notStarted" &&
                  <Box sx={{display:"flex", alignItems:"center", gap:"6px", height:"100%"}}>
                    <Box sx={{position:"relative", height:"20px"}}>
                      <img src={sablierIcon} style={{height:"18px", marginLeft:"3px"}} />
                    </Box>
                    <Box sx={{fontSize:"17px"}}>
                      <Hidden mdDown>
                        {languageValues?.adminDashboard?.monitoringTable?.statusNotStarted[actLanguage]}
                      </Hidden>
                      <Hidden mdUp>
                        {selectedID.statusProgress}%
                      </Hidden>
                    </Box>
                  </Box>
                }
              </Box>
            </Box>
            <Box sx={{width:['50%', '100%'], verflow:"hidden", textOverflow:"ellipsis", display:"flex", alignItems:["start", "center"], flexDirection:"column", gap:"10px"}}>
              <Box sx={{fontSize:"15px", color:"text.subTitle", textAlign:"center"}}>
                {languageValues?.adminDashboard?.monitoringTable?.level[actLanguage]}
              </Box>
              <Box sx={{fontSize:"17px", color:"black", textAlign:"center"}}>
                {selectedID?.level === "notAcquired" &&
                  <Box sx={{display:"flex", alignItems:"center", gap:"6px", height:"100%",}}>
                    <img src={closeRedRoundIcon} style={{width:"20px"}}/>
                    <Box sx={{fontSize:"17px"}}>
                      <Hidden only={["sm"]}>
                        <font>
                          {languageValues?.adminDashboard?.levelTypes?.notAcquired[actLanguage]}
                        </font>
                      </Hidden>
                    </Box>
                  </Box>
                }
                {selectedID?.level === "rattrapage" &&
                  <Box sx={{display:"flex", alignItems:"center", gap:"6px", height:"100%",}}>
                    <img src={closeRedRoundIcon} style={{width:"20px"}}/>
                    <Box sx={{fontSize:"17px"}}>
                      <Hidden only={["sm"]}>
                        <font>
                          {languageValues?.adminDashboard?.levelTypes?.rattrapage[actLanguage]}
                        </font>
                      </Hidden>
                    </Box>
                  </Box>
                }
                {selectedID?.level === "inProgress" &&
                  <Box sx={{display:"flex", alignItems:"center", gap:"6px", height:"100%"}}>
                    {languageValues?.adminDashboard?.levelTypes?.inProgress[actLanguage]}
                  </Box>
                }
                {selectedID?.level === "acquired" &&
                  <Box sx={{display:"flex", alignItems:"center", gap:"6px", height:"100%"}}>
                    <img src={checkGreenRoundIcon} style={{width:"20px"}}/>
                    <Box sx={{fontSize:"17px"}}>
                      <Hidden only={["sm"]}>
                        {languageValues?.adminDashboard?.levelTypes?.acquired[actLanguage]}
                      </Hidden>
                    </Box>
                  </Box>
                }
              </Box>
            </Box>
          </Box>
          <Box sx={{display:"flex", width:"100%"}}>
            <Box sx={{width:"100%", marginTop:"-50px"}}>
              <NewCertifChart
                datas={donneesRadar}
              />
              <LegendeRadar donnees={donneesRadar} />
            </Box>
          </Box>
        </Box>
      }

    </Box>
  )
}

const CertifModal = ({isOpen, onClose, selectedID, dataLoaded, getDatasForChart, aUneCertification}) => {

  return (
    <>
      <Hidden smUp>
        <Drawer
          anchor='bottom'
          open={isOpen}
          onClose={onClose}
          sx={{
            //background:"transparent",
          }}
          PaperProps={{
            sx: {
              height: 'calc(100% - 105px)',
              width : 'calc(100vw)',
              top: '105px',
              left : '0px',
              backgroundColor: "background.grey",
              //overflow:"hidden"
            },
          }}
        >
          <DialogContent
            onClose={onClose}
            selectedID={selectedID}
            dataLoaded={dataLoaded}
            getDatasForChart={getDatasForChart}
            aUneCertification={aUneCertification}
          />
        </Drawer>
      </Hidden>


      <Hidden smDown lgUp>
        <Dialog
          fullWidth={true}
          maxWidth="sm"
          open={isOpen}
          onClose={onClose}
          PaperProps={{
            sx : {
              borderRadius:"0px",
              border:"1px solid black",
              overflow:"hidden"
            }
          }}
        >
          <DialogContent
            onClose={onClose}
            selectedID={selectedID}
            dataLoaded={dataLoaded}
            getDatasForChart={getDatasForChart}
            aUneCertification={aUneCertification}
          />
        </Dialog>
      </Hidden>
    </>
  )
}


export default CertifModal
